/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext, HousePlanFinderListItem } from '@gh/builder-shared';
import { QuotationBase, SalesFolderStatus, SystemRoleBit } from '@gh/builder-shared-data';
import {
  AbstractSnapshotFormComponent,
  DictionaryItem,
  EnumDictionary,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteInitData,
  IS_REGION_AU_OR_NZ,
  IS_REGION_US,
  NumberTypes,
  toAppFormGroup,
  updateChildrenSchema,
  whenCreateMode,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { AutocompleteLoadMoreEvent } from '@gh/core-ui';
import { coalesce, isNotNil } from '@gh/core-util';
import { QuoteDetailsBusinessService } from '../../../resources/quote-details-business.service';
import {
  QuoteDetailsDerivedInfo,
  QuoteDetailsMasterInfo,
  QuoteSalesTaxRate,
  SiteListItem,
} from '../../../resources/quote.model';
import { QuoteDetailsSettings } from '../quote-details.state';

const getMasterInfoSchemaConfig = (masterInfo: QuoteDetailsMasterInfo) => ({
  children: {
    description: { required: true },
    priceBook: { required: true, enabled: whenCreateMode() },
    base: { required: true, enabled: [whenCreateMode(), isNotNil(masterInfo.priceBook)] },
    site: { required: true },
    baseHousePlan: { required: true, enabled: [whenCreateMode(), isNotNil(masterInfo.base)] },
    housePlan: void 0,
    salesTaxRates: void 0,
    isRounded: void 0,
  },
});

@Component({
  standalone: false,
  selector: 'gh-quote-details-top-form',
  templateUrl: 'quote-details-top-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteDetailsTopFormComponent extends AbstractSnapshotFormComponent<QuoteDetailsMasterInfo>
  implements OnInit {

  @Input() committed: QuoteDetailsMasterInfo;
  @Input() settings: QuoteDetailsSettings;
  @Input() derivedInfo: QuoteDetailsDerivedInfo;

  @Output() searchHousePlan = new EventEmitter<void>();
  @Output() openGto = new EventEmitter<any>();
  @Output() openTops = new EventEmitter<any>();
  @Output() openHpe = new EventEmitter<void>();
  @Output() openStr = new EventEmitter<void>();

  quotationBase: EnumDictionary<QuotationBase>;
  sites: SiteListItem[];
  selectedSite: string;

  constructor(private route: ActivatedRoute,
              private formStateBuilder: FormStateBuilder,
              private businessService: QuoteDetailsBusinessService,
              private userContext: BuilderUserContext) {
    super();
  }

  get isSalesEstimate(): boolean {
    return this.committed.base === QuotationBase.SalesEstimate;
  }

  get isQuickQuote(): boolean {
    return this.committed.base === QuotationBase.QuickQuote;
  }

  get housePlanCode(): string {
    const housePlan = this.committed.housePlan;

    return housePlan ? housePlan.code : '';
  }

  get canOpenSe(): boolean {
    return !this.userContext.hasSystemRole(SystemRoleBit.SalesPerson) || this.settings.isSalesEstimateShown;
  }

  get usRegion(): boolean {
    return IS_REGION_US;
  }

  get auOrNzRegion(): boolean {
    return IS_REGION_AU_OR_NZ;
  }

  get isDisabledGto(): boolean {
    return this.derivedInfo.salesFolderStatus === SalesFolderStatus.Completed || !!this.derivedInfo.rfqNumber;
  }

  getSalesTaxRatesLabel(str: QuoteSalesTaxRate[]): string {
    return str.map((rate) => NumberTypes.Rate.format(rate.ratePct) + '%').join('; ');
  }

  getHousePlanTitle(hp?: HousePlanFinderListItem): string {
    return hp ? `${hp.code}: ${hp.name}` : '';
  }

  ngOnInit(): void {
    super.ngOnInit();

    const { quotationBase, sites } = getRouteInitData(this.route);
    // filter quotations based on user settings:
    // sales estimates and quick quotes shown only when it is allowed in system variables
    if (this.form.createMode) {
      this.quotationBase = quotationBase.filter((item: DictionaryItem<QuotationBase>) => coalesce(
        {
          [QuotationBase.SalesEstimate]: this.settings.useSalesEstimate,
          [QuotationBase.QuickQuote]: this.settings.isQuickQuotesAllowed,
        }[item.id],
        true));
    } else {
      this.quotationBase = quotationBase;
    }
    this.sites = sites;
  }

  loadPriceBook(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.findPriceBooksByName(event.query));
  }

  onOpenTops(): void {
    this.openTops.emit({
      formMode: this.mode,
    });
  }

  onOpenGto(): void {
    this.openGto.emit({
      isDisabledGto: this.isDisabledGto,
    });
  }

  protected buildForm(snapshot: FormStateSnapshot<QuoteDetailsMasterInfo>): FormState<QuoteDetailsMasterInfo> {
    const masterInfo = getFormStateSnapshotValue(snapshot);

    return this.formStateBuilder.buildPlain({
      snapshot,
      mode: this.mode,
      enabled: whenCreateOrEditMode(),
      children: getMasterInfoSchemaConfig(masterInfo).children,
    });
  }

  protected afterRestored(): void {
    updateChildrenSchema(toAppFormGroup(this.form.group), getMasterInfoSchemaConfig(this.form.group.value).children);
  }
}
